import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Snackbar,
  Alert,
  DialogActions,
} from '@mui/material';
import { fetchApi , uploadBase64Logo} from '../services/api';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';

interface Company {
  거래처번호: number;
  업체번호: number;
  거래처명: string;
  담당자명: string;
  대표자: string;
  대표전화: string;
  대표이메일: string;
  기본주소: string;
  상세주소: string;
  거래처로고: string;
}

interface CompanyDetailDialogProps {
  open: boolean;
  onClose: () => void;
  company: Company | null;
  onSave: (action: string) => void;
}

const CompanyDetailDialog: React.FC<CompanyDetailDialogProps> = ({
  open,
  onClose,
  company,
  onSave,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const initialFormState = {
    거래처번호: 0,
    업체번호: Number(localStorage.getItem('CompanyNo')),
    거래처명: '',
    담당자명: '',
    대표자: '',
    대표전화: '',
    대표이메일: '',
    기본주소: '',
    상세주소: '',
    거래처로고: '',
  };

  const [formData, setFormData] = useState<Company>(initialFormState);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (company && open) {
      setFormData(company);
      setLogoUrl(company.거래처로고);
    } else if (!company) {
      setFormData(initialFormState);
    }
  }, [company, open]);

  const handleChange = (field: keyof Company) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const uploadResponse = await uploadBase64Logo(file);
        if (!uploadResponse || uploadResponse.status !== 200) {
          throw new Error(`파일 업로드 실패: ${uploadResponse?.message || '알 수 없는 오류'}`);
        }
        
        // const logoUrl = uploadResponse.data.url.replace('https://kr.object.iwinv.kr/orgin/', '');
        const logoUrl = uploadResponse.data.url;
        setLogoUrl(logoUrl);
        setFormData(prev => ({ ...prev, 거래처로고: logoUrl }));
      } catch (error) {
        console.error('로고 업로드 실패:', error);
        setSnackbarMessage('로고 업로드 중 오류가 발생했습니다.');
        setSnackbarOpen(true);
      }
    }
  };

  const handleLogoUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const url = event.target.value;
    setLogoUrl(url);
    setFormData(prev => ({ ...prev, 거래처로고: url }));
  };

  const handleSubmit = async () => {
    try {
      const response = await fetchApi('ITN_T_간편일정거래처저장', [
        { "컬럼명": "@거래처번호", "값": formData.거래처번호.toString() },
        { "컬럼명": "@업체번호", "값": formData.업체번호.toString() },
        { "컬럼명": "@거래처명", "값": formData.거래처명 },
        { "컬럼명": "@담당자명", "값": formData.담당자명 },
        { "컬럼명": "@대표자", "값": formData.대표자 },
        { "컬럼명": "@대표전화", "값": formData.대표전화 },
        { "컬럼명": "@대표이메일", "값": formData.대표이메일 },
        { "컬럼명": "@기본주소", "값": formData.기본주소 },
        { "컬럼명": "@상세주소", "값": formData.상세주소 },
        { "컬럼명": "@거래처로고", "값": formData.거래처로고 || '' },
      ]);

      if (response.data) {
        onClose();
        onSave('save');
      }
    } catch (error) {
      console.error('Failed to save company:', error);
      setSnackbarMessage('거래처 저장 중 오류가 발생했습니다.');
      setSnackbarOpen(true);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetchApi('ITN_T_간편일정거래처삭제', [
        { "컬럼명": "@거래처번호", "값": formData.거래처번호.toString() }
      ]);

      if (response.data) {
        setDeleteDialogOpen(false);
        onClose();
        onSave('delete');
      }
    } catch (error) {
      console.error('Failed to delete company:', error);
      setSnackbarMessage('거래처 삭제 중 오류가 발생했습니다.');
      setSnackbarOpen(true);
    }
  };

  const inputStyle = {
    height: '42px',
    '& .MuiOutlinedInput-root': {
      height: '42px'
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { maxWidth: isMobile ? '100%' : '800px' }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 1,
            justifyContent: 'space-between',
            alignItems: isMobile ? 'stretch' : 'center',
          }}>
            <Typography variant="h6">
              {company ? '거래처 정보 수정' : '신규 거래처 등록'}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              justifyContent: 'flex-end'
            }}>
              {company && (
                <Button
                  onClick={() => setDeleteDialogOpen(true)}
                  variant="contained"
                  sx={{ 
                    height: '36px',
                    flex: isMobile ? 1 : 'initial',
                    bgcolor: 'error.main', 
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'error.dark',
                    }
                  }}>
                  삭제
                </Button>
              )}
              <Button
                onClick={handleSubmit}
                variant="contained"
                sx={{ 
                  height: '36px',
                  flex: isMobile ? 1 : 'initial',
                  bgcolor: 'white', 
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'grey.100',
                  }
                }}>
                저장
              </Button>
              <Button 
                onClick={onClose}
                variant="contained"
                sx={{ 
                  height: '36px',
                  flex: isMobile ? 1 : 'initial',
                  bgcolor: 'white', 
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'grey.100',
                  }
                }}>
                닫기
              </Button>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={2} sx={{ pt: 4 }}>
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontSize: '13px' }}>
                  거래처명
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  required
                  value={formData.거래처명}
                  onChange={handleChange('거래처명')}
                  placeholder="거래처명을 입력하세요"
                  sx={inputStyle}
                />
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontSize: '13px' }}>
                  담당자명
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={formData.담당자명}
                  onChange={handleChange('담당자명')}
                  placeholder="담당자명을 입력하세요"
                  sx={inputStyle}
                />
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontSize: '13px' }}>
                  대표자
                </Typography>
                <TextField
                  fullWidth
                  required
                  size="small"
                  value={formData.대표자}
                  onChange={handleChange('대표자')}
                  placeholder="대표자명을 입력하세요"
                  sx={inputStyle}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontSize: '13px' }}>
                  대표전화
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={formData.대표전화}
                  onChange={handleChange('대표전화')}
                  placeholder="대표전화를 입력하세요"
                  sx={inputStyle}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontSize: '13px' }}>
                  대표이메일
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={formData.대표이메일}
                  onChange={handleChange('대표이메일')}
                  placeholder="대표이메일을 입력하세요"
                  sx={inputStyle}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontSize: '13px' }}>
                  기본주소
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={formData.기본주소}
                  onChange={handleChange('기본주소')}
                  placeholder="기본주소를 입력하세요"
                  sx={inputStyle}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontSize: '13px' }}>
                  상세주소
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={formData.상세주소}
                  onChange={handleChange('상세주소')}
                  placeholder="상세주소를 입력하세요"
                  sx={inputStyle}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sx={{ display: 'flex', gap: 3, alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ width: '70%' }}>
                <Box sx={{display: 'flex', gap: 3, alignItems: 'center' , justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontSize: '13px' }}>
                    거래처 로고
                  </Typography>
                  <Button
                      variant="contained"
                      onClick={() => fileInputRef.current?.click()}
                      sx={{ minWidth: '100px', height: '36px' }}
                    >
                      로고 선택
                    </Button>
                </Box>
                <Box >  
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={logoUrl}
                      onChange={handleLogoUrlChange}
                      placeholder="로고 URL을 입력하세요"
                      sx={inputStyle}
                    />
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept="image/*"
                      style={{ display: 'none' }}
                    />

                  </Box>
                </Box>
              </Box>
              {logoUrl && (
                  <Box sx={{  alignSelf: 'center'}}>
                    <img src={logoUrl} alt="거래처 로고" style={{ maxHeight: '80px', width: '100%' }} />
                  </Box>
                )}
              
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: 'error.main', 
          color: 'error.contrastText'
        }}>
          거래처 삭제
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography>
            정말 이 거래처를 삭제하시겠습니까?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleDelete}
            variant="contained"
            color="error"
          >
            삭제
          </Button>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            variant="contained"
            color="inherit"
          >
            취소
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="error"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CompanyDetailDialog; 